import {
  Sparkles, WalletCards, CalendarDays, Utensils, Bath,
  HeartPulse, PartyPopper, FolderLock, PackageCheck,
  HeartHandshake, MonitorPlay, ClipboardCheck, Bot, Settings,
  CalendarHeart, Images, Wrench, ClipboardList, ContactRound,
} from "lucide-react";

/* ════════════════════════════════════════════════════════════════════════
   HOME OS MODULE CONFIG

   HOME OS is the overarching operating system. Each entry below is a
   "section app" running inside the OS. Section identity (palette, icon,
   feeling, gradient) lives in:
     /app/frontend/src/pages/homeos/theme/homeOsSectionThemes.js

   This file keeps the same shape older components expect
   (`id`, `label`, `route`, `icon`, `accent`, `color.{tile,iconWrap,...}`)
   but every accent now maps to a deliberate, section-based palette —
   no random module colours.
   ════════════════════════════════════════════════════════════════════════ */

/** Static class bundles for each section accent.
 *  Strings must be literal so Tailwind's JIT can detect them. */
const ACCENT_BUNDLES = {
  teal: {
    tile:        "bg-teal-50 dark:bg-teal-900/15 border-teal-200 dark:border-teal-800/60",
    iconWrap:    "bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-300",
    activeBg:    "bg-teal-600 text-white border-teal-600",
    hoverBorder: "hover:border-teal-300 dark:hover:border-teal-600",
    ring:        "ring-teal-200 dark:ring-teal-800",
    dot:         "bg-teal-500",
    textAccent:  "text-teal-700 dark:text-teal-300",
  },
  emerald: {
    tile:        "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-800/60",
    iconWrap:    "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
    activeBg:    "bg-emerald-700 text-white border-emerald-700",
    hoverBorder: "hover:border-emerald-300 dark:hover:border-emerald-600",
    ring:        "ring-emerald-200 dark:ring-emerald-800",
    dot:         "bg-emerald-600",
    textAccent:  "text-emerald-700 dark:text-emerald-300",
  },
  indigo: {
    tile:        "bg-indigo-50 dark:bg-indigo-900/15 border-indigo-200 dark:border-indigo-800/60",
    iconWrap:    "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300",
    activeBg:    "bg-indigo-600 text-white border-indigo-600",
    hoverBorder: "hover:border-indigo-300 dark:hover:border-indigo-600",
    ring:        "ring-indigo-200 dark:ring-indigo-800",
    dot:         "bg-indigo-500",
    textAccent:  "text-indigo-700 dark:text-indigo-300",
  },
  amber: {
    tile:        "bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800/60",
    iconWrap:    "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
    activeBg:    "bg-amber-600 text-white border-amber-600",
    hoverBorder: "hover:border-amber-300 dark:hover:border-amber-600",
    ring:        "ring-amber-200 dark:ring-amber-800",
    dot:         "bg-amber-500",
    textAccent:  "text-amber-700 dark:text-amber-300",
  },
  cyan: {
    tile:        "bg-cyan-50 dark:bg-cyan-900/15 border-cyan-200 dark:border-cyan-800/60",
    iconWrap:    "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-300",
    activeBg:    "bg-cyan-600 text-white border-cyan-600",
    hoverBorder: "hover:border-cyan-300 dark:hover:border-cyan-600",
    ring:        "ring-cyan-200 dark:ring-cyan-800",
    dot:         "bg-cyan-500",
    textAccent:  "text-cyan-700 dark:text-cyan-300",
  },
  sky: {
    tile:        "bg-sky-50 dark:bg-sky-900/15 border-sky-200 dark:border-sky-800/60",
    iconWrap:    "bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300",
    activeBg:    "bg-sky-600 text-white border-sky-600",
    hoverBorder: "hover:border-sky-300 dark:hover:border-sky-600",
    ring:        "ring-sky-200 dark:ring-sky-800",
    dot:         "bg-sky-500",
    textAccent:  "text-sky-700 dark:text-sky-300",
  },
  rose: {
    tile:        "bg-rose-50 dark:bg-rose-900/15 border-rose-200 dark:border-rose-800/60",
    iconWrap:    "bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300",
    activeBg:    "bg-rose-600 text-white border-rose-600",
    hoverBorder: "hover:border-rose-300 dark:hover:border-rose-600",
    ring:        "ring-rose-200 dark:ring-rose-800",
    dot:         "bg-rose-500",
    textAccent:  "text-rose-700 dark:text-rose-300",
  },
  orange: {
    tile:        "bg-orange-50 dark:bg-orange-900/15 border-orange-200 dark:border-orange-800/60",
    iconWrap:    "bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-300",
    activeBg:    "bg-orange-500 text-white border-orange-500",
    hoverBorder: "hover:border-orange-300 dark:hover:border-orange-600",
    ring:        "ring-orange-200 dark:ring-orange-800",
    dot:         "bg-orange-500",
    textAccent:  "text-orange-700 dark:text-orange-300",
  },
  lime: {
    tile:        "bg-lime-50 dark:bg-lime-900/15 border-lime-200 dark:border-lime-800/60",
    iconWrap:    "bg-lime-100 dark:bg-lime-900/40 text-lime-700 dark:text-lime-300",
    activeBg:    "bg-lime-600 text-white border-lime-600",
    hoverBorder: "hover:border-lime-300 dark:hover:border-lime-600",
    ring:        "ring-lime-200 dark:ring-lime-800",
    dot:         "bg-lime-500",
    textAccent:  "text-lime-700 dark:text-lime-300",
  },
  violet: {
    tile:        "bg-violet-50 dark:bg-violet-900/15 border-violet-200 dark:border-violet-800/60",
    iconWrap:    "bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300",
    activeBg:    "bg-violet-600 text-white border-violet-600",
    hoverBorder: "hover:border-violet-300 dark:hover:border-violet-600",
    ring:        "ring-violet-200 dark:ring-violet-800",
    dot:         "bg-violet-500",
    textAccent:  "text-violet-700 dark:text-violet-300",
  },
  purple: {
    tile:        "bg-purple-50 dark:bg-purple-900/15 border-purple-200 dark:border-purple-800/60",
    iconWrap:    "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300",
    activeBg:    "bg-purple-700 text-white border-purple-700",
    hoverBorder: "hover:border-purple-300 dark:hover:border-purple-600",
    ring:        "ring-purple-200 dark:ring-purple-800",
    dot:         "bg-purple-600",
    textAccent:  "text-purple-700 dark:text-purple-300",
  },
  pink: {
    tile:        "bg-pink-50 dark:bg-pink-900/15 border-pink-200 dark:border-pink-800/60",
    iconWrap:    "bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-300",
    activeBg:    "bg-pink-600 text-white border-pink-600",
    hoverBorder: "hover:border-pink-300 dark:hover:border-pink-600",
    ring:        "ring-pink-200 dark:ring-pink-800",
    dot:         "bg-pink-500",
    textAccent:  "text-pink-700 dark:text-pink-300",
  },
  slate: {
    tile:        "bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700",
    iconWrap:    "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300",
    activeBg:    "bg-slate-800 text-white border-slate-800",
    hoverBorder: "hover:border-slate-300 dark:hover:border-slate-600",
    ring:        "ring-slate-200 dark:ring-slate-700",
    dot:         "bg-slate-500",
    textAccent:  "text-slate-700 dark:text-slate-300",
  },
};

function buildAccent(c) {
  return ACCENT_BUNDLES[c] || ACCENT_BUNDLES.teal;
}

export const HOME_OS_MODULES = {
  /* ─── OS HOME — Today's Overview / Mothership (includes wall mode) ─── */
  today: {
    id: "today", label: "Today's Overview", route: "/home-os",
    icon: Sparkles, accent: "teal",
    section: "today",
    description: "Household command centre, daily rhythm, ARA nudges.",
    color: buildAccent("teal"),
  },

  /* ─── Family Plan (dedicated /home-os/family-plan page) ─── */
  familyplan: {
    id: "familyplan", label: "Family Plan", route: "/home-os/family-plan",
    icon: ClipboardList, accent: "emerald",
    section: "familyplan",
    description: "Family charter, goals, roles and agreements.",
    color: buildAccent("emerald"),
  },

  /* ─── Financials (Money) ─── */
  money: {
    id: "money", label: "Financials", route: "/home-os/money",
    icon: WalletCards, accent: "emerald",
    section: "financial",
    description: "Money, budget, cashflow, goals, subscriptions.",
    color: buildAccent("emerald"),
  },

  /* ─── Schedules & Calendar ─── */
  calendar: {
    id: "calendar", label: "Schedules", route: "/home-os/calendar",
    icon: CalendarDays, accent: "indigo",
    section: "schedules",
    description: "Family calendar, routines, school, work, flow.",
    color: buildAccent("indigo"),
  },

  /* ─── Meals & Recipes ─── */
  meals: {
    id: "meals", label: "Meals", route: "/home-os/meals",
    icon: Utensils, accent: "amber",
    section: "meals",
    description: "Meal planning, recipes, shopping, nutrition.",
    color: buildAccent("amber"),
  },

  /* ─── Cleaning ─── */
  cleaning: {
    id: "cleaning", label: "Cleaning", route: "/home-os/cleaning",
    icon: Bath, accent: "cyan",
    section: "cleaning",
    description: "Room resets, chores, weekly cleaning rhythm.",
    color: buildAccent("cyan"),
  },

  /* ─── Maintenance (dedicated page) ─── */
  maintenance: {
    id: "maintenance", label: "Maintenance", route: "/home-os/maintenance",
    icon: Wrench, accent: "sky",
    section: "maintenance",
    description: "Repairs, safety checks, warranties and property projects.",
    color: buildAccent("sky"),
  },

  /* ─── Chores (lives under Cleaning section identity) ─── */
  chores: {
    id: "chores", label: "Chores", route: "/home-os/chores",
    icon: ClipboardCheck, accent: "cyan",
    section: "cleaning",
    description: "Daily and weekly chore allocation.",
    color: buildAccent("sky"),
  },

  /* ─── Health & Wellness ─── */
  health: {
    id: "health", label: "Health", route: "/home-os/health",
    icon: HeartPulse, accent: "rose",
    section: "health",
    description: "Appointments, meds, symptoms, care, wellness.",
    color: buildAccent("rose"),
  },

  /* ─── Inventory (formerly Supplies) ─── */
  supplies: {
    id: "supplies", label: "Inventory", route: "/home-os/supplies",
    icon: PackageCheck, accent: "lime",
    section: "supplies",
    description: "Pantry, household stock, low-stock and shopping.",
    color: buildAccent("lime"),
  },

  /* ─── Contacts (dedicated page) ─── */
  contacts: {
    id: "contacts", label: "Contacts", route: "/home-os/contacts",
    icon: ContactRound, accent: "teal",
    section: "contacts",
    description: "Family members, household contacts and trusted people.",
    color: buildAccent("teal"),
  },

  /* ─── Documents & Admin ─── */
  documents: {
    id: "documents", label: "Documents", route: "/home-os/documents",
    icon: FolderLock, accent: "purple",
    section: "documents",
    description: "Forms, bills, renewals, insurance, records.",
    color: buildAccent("purple"),
  },

  /* ─── Grateful Tree (formerly Mood) ─── */
  mood: {
    id: "mood", label: "Grateful Tree", route: "/home-os/mood",
    icon: HeartHandshake, accent: "violet",
    section: "mood",
    description: "Daily check-ins, gratitude notes, family connection.",
    color: buildAccent("violet"),
  },

  /* ─── Family (visually grouped under Mood & Wellbeing) ─── */
  family: {
    id: "family", label: "Family", route: "/home-os/family",
    icon: HeartHandshake, accent: "violet",
    section: "mood",
    description: "Family members, profiles, avatars.",
    color: buildAccent("violet"),
  },

  /* ─── Wall Display — now a MODE of Today's Overview. Kept in config so
        the legacy /home-os/wall route still resolves cleanly. Not present
        in HOME_OS_MODULE_LIST (deduped from the sidebar/subnav). ─── */
  wall: {
    id: "wall", label: "Wall Display", route: "/home-os/wall",
    icon: MonitorPlay, accent: "slate",
    section: "today",
    description: "Wall Display mode of Today's Overview — kitchen-screen view.",
    color: buildAccent("slate"),
  },

  /* ─── AI Centre (treated as Today's sibling) ─── */
  ai: {
    id: "ai", label: "AI Centre", route: "/home-os/command-centre",
    icon: Bot, accent: "teal",
    section: "today",
    description: "ARA suggestions and OS-wide automations.",
    color: buildAccent("teal"),
  },

  /* ─── Weekly Report ─── */
  report: {
    id: "report", label: "Weekly Report", route: "/home-os/report",
    icon: CalendarHeart, accent: "indigo",
    section: "schedules",
    description: "Household rhythm, wins and resets.",
    color: buildAccent("indigo"),
  },

  /* ─── Holidays & Events (theme defined; route is a placeholder
        that points at Schedules until a dedicated page ships) ─── */
  holidays: {
    id: "holidays", label: "Holidays & Events", route: "/home-os/calendar",
    icon: PartyPopper, accent: "orange",
    section: "holidays",
    description: "Birthdays, Christmas, parties, gifts, travel.",
    color: buildAccent("orange"),
  },

  /* ─── Memories & Media ─── */
  memories: {
    id: "memories", label: "Memories & Media", route: "/home-os/memories",
    icon: Images, accent: "pink",
    section: "memories",
    description: "Photos, videos, albums and family memories.",
    color: buildAccent("pink"),
  },

  /* ─── Setup ─── */
  setup: {
    id: "setup", label: "Setup", route: "/home-os/setup",
    icon: Settings, accent: "slate",
    section: "today",
    description: "Household setup and member configuration.",
    color: buildAccent("slate"),
  },
};

/**
 * Canonical OS section order (sidebar + sub-nav + bento).
 * Today is always the home screen.
 */
export const HOME_OS_MODULE_LIST = [
  "today",
  "familyplan",
  "calendar",
  "money",
  "meals",
  "cleaning",
  "maintenance",
  "health",
  "documents",
  "supplies",
  "contacts",
  "holidays",
  "memories",
  "mood",
].map((id) => HOME_OS_MODULES[id]);

/* Category → module mapping for task / event chips. */
export const CATEGORY_TO_MODULE = {
  Meals:     "meals",
  Chores:    "chores",
  Cleaning:  "cleaning",
  Calendar:  "calendar",
  Money:     "money",
  Supplies:  "supplies",
  Health:    "health",
  Documents: "documents",
  Transport: "calendar",
  School:    "family",
  Family:    "family",
  Admin:     "documents",
  Mood:      "mood",
};

export function moduleForCategory(category) {
  const id = CATEGORY_TO_MODULE[category] || "today";
  return HOME_OS_MODULES[id];
}
